import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, requestWithMessage } from '../lib/api';
import { formatBytes } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import {
  SITES_KEY,
  TEXT_EXTENSIONS,
  cleanPath,
  extensionOf,
  fileAddress,
  skipReason,
  starterPage,
  suggestFolder,
  type FolderCheck,
  type SiteDetails,
  type SiteFile,
} from '../lib/sites';
import { FormGrid, FormPage } from '../components/FormPage';
import { Badge, Button, Card, Checkbox, ErrorState, Field, Input, LoadingBlock, PageHeader, Textarea, useToast } from '../components/ui';

/**
 * Sites' forms — every one a page of its own (§ "every form is a page"): a new
 * site and a site's settings, deleting a site, a new folder, renaming or moving a
 * file, and the editor that writes a page, a style sheet or a script.
 */

function useSite(id: string) {
  return useQuery({
    queryKey: [...SITES_KEY, 'site', id],
    queryFn: () => api.get<SiteDetails>(`/sites/${id}`),
    enabled: !!id,
  });
}

/** The site's page, at a folder of it. */
const sitePage = (id: string, folder = '') => (folder ? `/sites/${id}?folder=${encodeURIComponent(folder)}` : `/sites/${id}`);

const FOLDER_PROBLEM: Record<NonNullable<FolderCheck['problem']>, TranslationKey> = {
  EMPTY: 'sites.folderEmpty',
  TOO_SHORT: 'sites.folderTooShort',
  TOO_LONG: 'sites.folderTooLong',
  SHAPE: 'sites.folderShape',
  RESERVED: 'sites.folderReserved',
  TAKEN: 'sites.folderTaken',
};

const PATH_PROBLEM: Record<string, TranslationKey> = {
  HIDDEN: 'sites.skipHidden',
  TYPE: 'sites.skipType',
  NAME: 'sites.skipName',
};

// ---------------------------------------------------------------------------
// A new site, and a site's settings
// ---------------------------------------------------------------------------

/** `/sites/new` and `/sites/:id/edit`: its name, its folder — the address — and, once made, whether it is up. */
export function SiteFormPage() {
  const { id = '' } = useParams();
  const editing = !!id;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const existing = useSite(id);

  const [form, setForm] = useState({ name: '', folder: '', description: '', isLive: true, searchable: false });
  // A new site's folder follows its name until the folder is typed in.
  const [folderTyped, setFolderTyped] = useState(editing);
  useEffect(() => {
    const s = existing.data;
    if (s) setForm({ name: s.name, folder: s.folder, description: s.description ?? '', isLive: s.isLive, searchable: s.searchable });
  }, [existing.data]);

  const folder = form.folder.trim().toLowerCase();
  const check = useFolderCheck(folder, editing ? id : undefined);
  const moved = editing && existing.data && folder !== existing.data.folder;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        folder,
        description: form.description.trim() || null,
        ...(editing ? { isLive: form.isLive, searchable: form.searchable } : {}),
      };
      return editing
        ? requestWithMessage<SiteDetails>(`/sites/${id}`, { method: 'PATCH', body })
        : requestWithMessage<SiteDetails>('/sites', { method: 'POST', body });
    },
    onSuccess: ({ data }) => {
      void queryClient.invalidateQueries({ queryKey: SITES_KEY });
      toast.success(t(editing ? 'sites.saved' : 'sites.created'));
      navigate(`/sites/${data.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const problem = check.data?.folder === folder ? check.data.problem : null;
  return (
    <FormPage
      title={editing ? t('sites.settingsTitle', { name: existing.data?.name ?? '' }) : t('sites.newTitle')}
      subtitle={editing ? undefined : t('sites.newSubtitle')}
      backTo={editing ? `/sites/${id}` : '/sites'}
      backLabel={editing ? (existing.data?.name ?? t('sites.title')) : t('sites.title')}
      submitLabel={editing ? t('common.save') : t('sites.create')}
      onSubmit={() => save.mutate()}
      submitting={save.isPending}
      disabled={!form.name.trim() || !folder || !!problem}
      loading={editing && existing.isLoading}
      error={editing ? existing.error : undefined}
      onRetry={() => void existing.refetch()}
    >
      <div className="space-y-4">
        <FormGrid>
          <Field label={t('sites.name')} required>
            <Input
              value={form.name}
              maxLength={120}
              autoFocus={!editing}
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({ ...f, name, folder: folderTyped ? f.folder : suggestFolder(name) }));
              }}
            />
          </Field>
          <Field
            label={t('sites.folder')}
            required
            error={problem ? t(FOLDER_PROBLEM[problem]) : null}
            hint={t('sites.folderHint')}
          >
            <Input
              value={form.folder}
              maxLength={63}
              dir="ltr"
              spellCheck={false}
              autoCapitalize="off"
              onChange={(e) => {
                setFolderTyped(true);
                setForm((f) => ({ ...f, folder: e.target.value }));
              }}
            />
          </Field>
        </FormGrid>
        {folder && check.data && !problem && (
          <p className="text-body-sm text-muted">
            {t('sites.addressWillBe')}{' '}
            <span dir="ltr" className="font-medium text-[var(--text-primary)]">
              {check.data.address}
            </span>
          </p>
        )}
        {moved && !problem && <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('sites.folderMoves')}</p>}
        <Field label={t('sites.description')} hint={t('sites.descriptionHint')}>
          <Textarea
            value={form.description}
            maxLength={1000}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </Field>
        {editing && (
          <div className="space-y-3 border-t hairline pt-4">
            <div>
              <Checkbox
                checked={form.isLive}
                onChange={(e) => setForm((f) => ({ ...f, isLive: e.target.checked }))}
                label={t('sites.isLive')}
              />
              <p className="ms-6 mt-0.5 text-caption text-faint">{t('sites.isLiveHint')}</p>
            </div>
            <div>
              <Checkbox
                checked={form.searchable}
                onChange={(e) => setForm((f) => ({ ...f, searchable: e.target.checked }))}
                label={t('sites.searchable')}
              />
              <p className="ms-6 mt-0.5 text-caption text-faint">{t('sites.searchableHint')}</p>
            </div>
          </div>
        )}
      </div>
    </FormPage>
  );
}

/** Whether a folder is free, a moment after typing stops. */
function useFolderCheck(folder: string, siteId?: string) {
  const [asked, setAsked] = useState(folder);
  useEffect(() => {
    const timer = setTimeout(() => setAsked(folder), 300);
    return () => clearTimeout(timer);
  }, [folder]);
  return useQuery({
    queryKey: [...SITES_KEY, 'folder-check', asked, siteId ?? null],
    queryFn: () => api.get<FolderCheck>('/sites/folder-check', { folder: asked, siteId }),
    enabled: !!asked,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Deleting a site
// ---------------------------------------------------------------------------

/** `/sites/:id/delete`: everything in it goes for good, once its folder is typed to say so. */
export function SiteDeletePage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const site = useSite(id);
  const [typed, setTyped] = useState('');

  const remove = useMutation({
    mutationFn: () => requestWithMessage(`/sites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SITES_KEY });
      toast.success(t('sites.siteDeleted'));
      navigate('/sites');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = site.data;
  return (
    <FormPage
      title={t('sites.deleteTitle', { name: data?.name ?? '' })}
      backTo={`/sites/${id}`}
      backLabel={data?.name ?? t('sites.title')}
      submitLabel={t('sites.deleteSubmit')}
      destructive
      onSubmit={() => remove.mutate()}
      submitting={remove.isPending}
      disabled={!data || typed.trim().toLowerCase() !== data.folder}
      loading={site.isLoading}
      error={site.error}
      onRetry={() => void site.refetch()}
      summary={
        data && (
          <div className="space-y-1 text-body-sm">
            <p dir="ltr" className="font-medium text-[var(--text-primary)]">
              {data.address}
            </p>
            <p className="text-muted">{t('sites.summary', { n: data.fileCount, size: formatBytes(data.bytes, locale), max: data.maxFiles })}</p>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <p className="text-body text-[var(--text-secondary)]">{t('sites.deleteWarning')}</p>
        <Field label={t('sites.deleteConfirm', { folder: data?.folder ?? '' })}>
          <Input value={typed} dir="ltr" spellCheck={false} autoCapitalize="off" onChange={(e) => setTyped(e.target.value)} />
        </Field>
      </div>
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// A new folder
// ---------------------------------------------------------------------------

/**
 * `/sites/:id/new-folder?in=`: a folder is where files go — it is there once a
 * file is in it, so this opens it, empty, ready for files dropped on it.
 */
export function SiteNewFolderPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const parent = cleanPath(params.get('in') ?? '');
  const { t } = useI18n();
  const navigate = useNavigate();
  const site = useSite(id);
  const [name, setName] = useState('');
  const path = cleanPath(parent ? `${parent}/${name}` : name);
  // A folder's name follows a file's rules — no type of its own.
  const problem = name.trim() && skipReason(`${path}/index.html`);
  const taken = !!site.data?.files.some((f) => f.path === path);

  return (
    <FormPage
      title={t('sites.newFolder')}
      subtitle={parent ? t('sites.inFolder', { folder: parent }) : undefined}
      backTo={sitePage(id, parent)}
      backLabel={site.data?.name ?? t('sites.title')}
      submitLabel={t('sites.createFolder')}
      onSubmit={() => navigate(sitePage(id, path))}
      disabled={!name.trim() || name.includes('/') || !!problem || taken}
      loading={site.isLoading}
      error={site.error}
    >
      <Field
        label={t('sites.folderName')}
        hint={t('sites.newFolderHint')}
        error={problem ? t(PATH_PROBLEM[problem] ?? 'sites.skipName') : taken ? t('sites.pathTaken') : name.includes('/') ? t('sites.skipName') : null}
      >
        <Input value={name} dir="ltr" autoFocus spellCheck={false} onChange={(e) => setName(e.target.value)} />
      </Field>
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// Renaming or moving a file
// ---------------------------------------------------------------------------

/** `/sites/:id/files/:fileId/move`: a new path in the site — a new name, another folder, or both. */
export function SiteFileMovePage() {
  const { id = '', fileId = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const site = useSite(id);
  const file = site.data?.files.find((f) => f.id === fileId);
  const [path, setPath] = useState('');
  const initial = file?.path;
  useEffect(() => {
    if (initial !== undefined) setPath((typed) => typed || initial);
  }, [initial]);

  const target = cleanPath(path);
  const problem = target ? skipReason(target) : null;
  const taken = !!site.data?.files.some((f) => f.path === target && f.id !== fileId);
  const folderOf = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');

  const move = useMutation({
    mutationFn: () => requestWithMessage<SiteFile>(`/sites/${id}/files/${fileId}`, { method: 'PATCH', body: { path: target } }),
    onSuccess: ({ data }) => {
      void queryClient.invalidateQueries({ queryKey: SITES_KEY });
      toast.success(t('sites.saved'));
      navigate(sitePage(id, folderOf(data.path)));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('sites.moveTitle', { name: file?.path ?? '' })}
      backTo={sitePage(id, file ? folderOf(file.path) : '')}
      backLabel={site.data?.name ?? t('sites.title')}
      submitLabel={t('common.save')}
      onSubmit={() => move.mutate()}
      submitting={move.isPending}
      disabled={!target || !!problem || taken || target === file?.path}
      loading={site.isLoading}
      error={site.error ?? (site.data && !file ? new Error(t('sites.fileGone')) : undefined)}
    >
      <Field
        label={t('sites.path')}
        hint={t('sites.pathHint')}
        error={problem ? t(PATH_PROBLEM[problem]) : taken ? t('sites.pathTaken') : null}
      >
        <Input value={path} dir="ltr" spellCheck={false} onChange={(e) => setPath(e.target.value)} />
      </Field>
      {file && extensionOf(target) !== extensionOf(file.path) && !problem && (
        <p className="mt-3 text-body-sm text-warning-fg dark:text-warning-dark">{t('sites.typeChanges')}</p>
      )}
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

/**
 * `/sites/:id/edit-file?path=` and `/sites/:id/new-file?folder=`: a page, a style
 * sheet or a script, written here. Saving keeps it as any upload is, and stays on
 * the page — Ctrl+S too — so a page is written, saved and looked at in a loop.
 * Saved over someone else's newer save, it is refused rather than lost.
 */
export function SiteEditorPage({ creating = false }: { creating?: boolean }) {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const site = useSite(id);
  const folder = cleanPath(params.get('folder') ?? '');
  const openPath = creating ? '' : cleanPath(params.get('path') ?? '');
  const file = site.data?.files.find((f) => f.path === openPath);

  // Read once: a save here moves the version on, and reading it again would undo what was typed since.
  const opened = useQuery({
    queryKey: [...SITES_KEY, 'text', id, file?.id],
    queryFn: () => api.get<SiteFile & { content: string }>(`/sites/${id}/files/${file!.id}/text`),
    enabled: !!file,
    staleTime: Infinity,
    gcTime: 0,
  });

  const [name, setName] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (opened.data) {
      setContent(opened.data.content);
      setSaved(opened.data.content);
      setVersion(opened.data.version);
    }
  }, [opened.data]);

  const path = creating ? cleanPath(folder ? `${folder}/${name}` : name) : openPath;
  const ext = extensionOf(path);
  const nameProblem = creating && name.trim() ? skipReason(path) ?? (TEXT_EXTENSIONS.has(ext) ? null : 'TEXT') : null;
  const taken = creating && !!site.data?.files.some((f) => f.path === path);
  const text = content ?? '';
  const dirty = content !== null && content !== saved;

  // A new page starts from a page's first lines; a style sheet or a script, from nothing — until it is typed in.
  const starter = site.data ? starterPage(site.data.name) : '';
  useEffect(() => {
    if (!creating) return;
    const page = ext === '.html' || ext === '.htm';
    setContent((c) => (c === null || c === '' || c === starter ? (page ? starter : '') : c));
  }, [creating, ext, starter]);

  // Leaving with changes unsaved asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: () =>
      requestWithMessage<SiteFile>(`/sites/${id}/text`, { method: 'PUT', body: { path, content: text, version: creating ? null : version } }),
    onSuccess: async ({ data }) => {
      setSaved(text);
      setVersion(data.version);
      toast.success(t('sites.fileSaved'));
      void queryClient.invalidateQueries({ queryKey: [...SITES_KEY, 'list'] });
      // Made: from now on it is the file being edited — once the site's files say it is there.
      await queryClient.refetchQueries({ queryKey: [...SITES_KEY, 'site', id] });
      if (creating) navigate(`/sites/${id}/edit-file?path=${encodeURIComponent(data.path)}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError && e.code === 'SITE_FILE_CHANGED' ? t('sites.fileChanged') : e.message),
  });
  const canSave = !!path && !nameProblem && !taken && (creating ? true : dirty) && !save.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canSave) save.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSave, save]);

  const back = sitePage(id, creating ? folder : openPath.includes('/') ? openPath.slice(0, openPath.lastIndexOf('/')) : '');
  const bytes = useMemo(() => new TextEncoder().encode(text).length, [text]);

  if (site.isLoading || (file && opened.isLoading)) return <LoadingBlock />;
  if (site.error || opened.error) return <ErrorState error={site.error ?? opened.error} onRetry={() => void opened.refetch()} />;
  if (!creating && !file) return <ErrorState error={new Error(t('sites.fileGone'))} />;

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {site.data?.name}
        </Link>
      </div>
      <PageHeader
        title={creating ? t('sites.newFile') : <span dir="ltr">{openPath}</span>}
        subtitle={
          creating ? (folder ? t('sites.inFolder', { folder }) : t('sites.newFileHint')) : dirty ? (
            <Badge tone="warning">{t('sites.unsaved')}</Badge>
          ) : undefined
        }
        action={
          !creating &&
          site.data && (
            <a href={fileAddress(site.data, openPath)} target="_blank" rel="noreferrer">
              <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />}>{t('sites.openFile')}</Button>
            </a>
          )
        }
      />
      <Card>
        {creating && (
          <Field
            label={t('sites.fileName')}
            hint={t('sites.fileNameHint')}
            error={
              nameProblem === 'TEXT'
                ? t('sites.notText')
                : nameProblem
                  ? t(PATH_PROBLEM[nameProblem] ?? 'sites.skipName')
                  : taken
                    ? t('sites.pathTaken')
                    : null
            }
            className="mb-4 max-w-md"
          >
            <Input value={name} dir="ltr" autoFocus spellCheck={false} placeholder="index.html" onChange={(e) => setName(e.target.value)} />
          </Field>
        )}
        <label className="block">
          <span className="sr-only">{t('sites.content')}</span>
          <textarea
            value={text}
            onChange={(e) => setContent(e.target.value)}
            dir="ltr"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            wrap="off"
            className="block h-[60vh] min-h-[20rem] w-full resize-y rounded-md border hairline bg-[var(--surface-sunken)] p-3 font-mono text-body-sm leading-relaxed text-[var(--text-primary)] focus:border-primary"
          />
        </label>
        <p className="mt-2 text-caption text-faint">{t('sites.editorHint', { size: formatBytes(bytes, locale) })}</p>
      </Card>
      <div
        data-form-actions
        className="sticky bottom-0 z-sticky mt-5 -mx-4 border-t hairline bg-[var(--surface-page)]/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6"
      >
        <div className="flex justify-end gap-2">
          <Link to={back}>
            <Button>{t('common.cancel')}</Button>
          </Link>
          <Button variant="primary" icon={<Save className="h-4 w-4" aria-hidden />} loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
